import { useState, useEffect } from 'react';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Plus, UtensilsCrossed, Pencil, Trash2, Power, Hotel, FolderCog } from 'lucide-react';
import { toast } from 'sonner';
import { useUser } from '@/utils/userHelper';
import { useTabs, TAB_TYPES } from '@/context/TabsContext';

export default function MenuManagement({ user: propUser, isTab, tabId, tabData }) {
  const user = useUser(propUser);
  const { openTab } = useTabs();
  const restaurantId = tabData?.restaurantId;
  const restaurant = tabData?.restaurant;
  
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category_id: '',
    category: '',
    price: '',
    description: '',
    available_for_room_service: false,
    prep_time: '',
    serve_time: ''
  });

  const canManage = ['super_admin', 'hotel_admin', 'manager'].includes(user?.role);

  // Open Menu Categories in a new tab
  const openCategoriesTab = () => {
    openTab({
      type: TAB_TYPES.PAGE_MENU_CATEGORIES,
      id: 'menu-categories',
      title: 'Menu Categories',
      data: {}
    });
  };

  useEffect(() => {
    loadData();
  }, [restaurantId]);

  const loadData = async () => {
    try {
      // ALWAYS filter by restaurant to prevent cross-restaurant data leakage
      if (restaurantId) {
        // Get menu items and categories for this specific restaurant only
        const [menuRes, catRes] = await Promise.all([
          api.get(`/restaurants/${restaurantId}/menu`),
          api.get(`/restaurants/${restaurantId}/categories`)
        ]);
        setItems(menuRes.data || []);
        setCategories((catRes.data || []).filter(c => c.is_active !== false));
      } else {
        // No restaurant selected - show empty or prompt to select
        setItems([]);
        setCategories([]);
        toast.info('Please select a restaurant to view menu items');
      }
    } catch (error) {
      console.error('Failed to load menu data:', error);
      toast.error('Failed to load menu data');
      setItems([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({ name: '', category_id: '', category: '', price: '', description: '', available_for_room_service: false, prep_time: '', serve_time: '' });
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      category_id: item.category_id || '',
      category: item.category,
      price: item.price.toString(),
      description: item.description || '',
      available_for_room_service: item.available_for_room_service || false,
      prep_time: item.prep_time?.toString() || '',
      serve_time: item.serve_time?.toString() || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!restaurantId) {
      toast.error('Please select a restaurant first');
      return;
    }
    
    try {
      const data = {
        name: formData.name,
        category_id: formData.category_id || null,
        category: formData.category,
        price: parseFloat(formData.price),
        description: formData.description,
        available_for_room_service: formData.available_for_room_service,
        prep_time: formData.prep_time ? parseInt(formData.prep_time) : null,
        serve_time: formData.serve_time ? parseInt(formData.serve_time) : null,
        restaurant_id: restaurantId
      };
      
      if (editingItem) {
        await api.put(`/menu/${editingItem.id}`, data);
        toast.success('Menu item updated');
      } else {
        // Use restaurant-specific endpoint to ensure proper isolation
        await api.post(`/restaurants/${restaurantId}/menu`, data);
        toast.success('Menu item added');
      }
      
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(editingItem ? 'Failed to update item' : 'Failed to add item');
    }
  };

  const handleCategoryChange = (categoryId) => {
    const selectedCat = categories.find(c => c.id === categoryId);
    setFormData({
      ...formData,
      category_id: categoryId,
      category: selectedCat ? selectedCat.name : ''
    });
  };

  const toggleAvailability = async (itemId) => {
    try {
      await api.patch(`/menu/${itemId}/toggle`);
      toast.success('Item status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const toggleRoomService = async (item) => {
    try {
      await api.put(`/menu/${item.id}`, {
        available_for_room_service: !item.available_for_room_service
      });
      toast.success(item.available_for_room_service ? 'Removed from Room Service' : 'Added to Room Service');
      loadData();
    } catch (error) {
      toast.error('Failed to update room service status');
    }
  };

  const deleteItem = async (itemId, itemName) => {
    if (!window.confirm(`Are you sure you want to delete "${itemName}"?`)) return;
    
    try {
      await api.delete(`/menu/${itemId}`);
      toast.success('Menu item deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const groupedItems = items.reduce((acc, item) => {
    const cat = item.category || 'Uncategorized';
    if (!acc[cat]) {
      acc[cat] = [];
    }
    acc[cat].push(item);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="menu-management">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Manrope' }}>Menu Items</h1>
          <p className="text-gray-500 text-sm mt-1">Manage restaurant menu items</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={openCategoriesTab} 
              className="border-orange-200 text-orange-600 hover:bg-orange-50" 
              data-testid="manage-categories-button"
            >
              <FolderCog className="h-4 w-4 mr-2" />
              Manage Categories
            </Button>
            <Button onClick={openAddDialog} className="bg-gradient-orange hover:opacity-90 text-white btn-glow" data-testid="add-menu-item-button">
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="bg-white" data-testid="menu-dialog">
          <DialogHeader>
            <DialogTitle className="text-gray-800">{editingItem ? 'Edit Menu Item' : 'Add Menu Item'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-gray-700">Item Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                className="border-orange-200 focus:border-orange-400"
                data-testid="menu-name-input"
              />
            </div>
            
            {/* Category Dropdown */}
            <div className="space-y-2">
              <Label className="text-gray-700">Category *</Label>
              {categories.length === 0 ? (
                <div className="space-y-2">
                  <p className="text-sm text-orange-600 p-2 bg-orange-50 rounded border border-orange-200">
                    No categories defined. Go to Master Data → Menu Categories first, or enter manually below.
                  </p>
                  <Input
                    placeholder="Enter category name"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    required
                    className="border-orange-200 focus:border-orange-400"
                  />
                </div>
              ) : (
                <Select 
                  value={formData.category_id} 
                  onValueChange={handleCategoryChange}
                >
                  <SelectTrigger className="border-orange-200" data-testid="menu-category-select">
                    <SelectValue placeholder="Select Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price" className="text-gray-700">Price (₹) *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: e.target.value})}
                  required
                  className="border-orange-200 focus:border-orange-400"
                  data-testid="menu-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description" className="text-gray-700">Description</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="border-orange-200 focus:border-orange-400"
                  data-testid="menu-description-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="prep_time" className="text-gray-700">Prep Time (mins)</Label>
                <Input
                  id="prep_time"
                  type="number"
                  min="0"
                  value={formData.prep_time}
                  onChange={(e) => setFormData({...formData, prep_time: e.target.value})}
                  placeholder="e.g., 15"
                  className="border-orange-200 focus:border-orange-400"
                  data-testid="menu-prep-time-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="serve_time" className="text-gray-700">Serve Time (mins)</Label>
                <Input
                  id="serve_time"
                  type="number"
                  min="0"
                  value={formData.serve_time}
                  onChange={(e) => setFormData({...formData, serve_time: e.target.value})}
                  placeholder="e.g., 5"
                  className="border-orange-200 focus:border-orange-400"
                  data-testid="menu-serve-time-input"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-violet-50 rounded-lg border border-violet-200">
              <Switch
                checked={formData.available_for_room_service}
                onCheckedChange={(checked) => setFormData({ ...formData, available_for_room_service: checked })}
                data-testid="item-room-service-switch"
              />
              <div>
                <Label className="text-violet-800 font-medium">Available for Room Service</Label>
                <p className="text-xs text-violet-600">This item will be shown in Guest Portal</p>
              </div>
            </div>
            
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-gradient-orange hover:opacity-90 text-white" data-testid="submit-menu-button">
                {editingItem ? 'Update Item' : 'Add Item'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-10 w-10 rounded-full border-4 border-orange-500 border-t-transparent animate-spin" />
        </div>
      ) : (
        <div className="space-y-6">
          {Object.keys(groupedItems).map(category => (
            <Card key={category} className="border-orange-100">
              <CardHeader className="pb-3">
                <CardTitle className="text-gray-800 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <div className="h-8 w-8 rounded-lg bg-orange-100 flex items-center justify-center">
                    <UtensilsCrossed className="h-4 w-4 text-orange-600" />
                  </div>
                  {category}
                  <Badge className="ml-2 bg-orange-100 text-orange-700 border-orange-200">{groupedItems[category].length} items</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {groupedItems[category].map(item => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-4 border border-orange-100 rounded-xl hover:bg-orange-50 transition-all"
                      data-testid={`menu-item-row-${item.id}`}
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-orange-100 to-purple-100 flex items-center justify-center">
                          <UtensilsCrossed className="h-5 w-5 text-orange-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-800">{item.name}</p>
                          {item.description && (
                            <p className="text-sm text-gray-500">{item.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="font-bold text-lg text-orange-600" style={{ fontFamily: 'JetBrains Mono' }}>
                          ₹{item.price}
                        </p>
                        {item.available_for_room_service && (
                          <Badge className="bg-violet-100 text-violet-700 border-violet-200">
                            <Hotel className="h-3 w-3 mr-1" />
                            Room Service
                          </Badge>
                        )}
                        <Badge 
                          className={item.is_available ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-red-100 text-red-700 border-red-200'}
                          data-testid={`menu-status-${item.id}`}
                        >
                          {item.is_available ? 'Active' : 'Inactive'}
                        </Badge>
                        {canManage && (
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => toggleRoomService(item)}
                              title={item.available_for_room_service ? 'Remove from Room Service' : 'Add to Room Service'}
                              className={`${item.available_for_room_service ? 'text-violet-600 bg-violet-50' : 'text-gray-400'} hover:text-violet-600 hover:bg-violet-50`}
                              data-testid={`toggle-room-service-${item.id}`}
                            >
                              <Hotel className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => toggleAvailability(item.id)}
                              title={item.is_available ? 'Deactivate' : 'Activate'}
                              className="text-gray-500 hover:text-orange-600"
                              data-testid={`toggle-menu-${item.id}`}
                            >
                              <Power className={`h-4 w-4 ${item.is_available ? 'text-emerald-600' : 'text-gray-400'}`} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditDialog(item)}
                              className="text-gray-500 hover:text-orange-600"
                              data-testid={`edit-menu-${item.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteItem(item.id, item.name)}
                              className="text-gray-500 hover:text-red-600 hover:bg-red-50"
                              data-testid={`delete-menu-${item.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!loading && items.length === 0 && (
        <Card className="border-orange-100">
          <CardContent className="py-12 text-center">
            <UtensilsCrossed className="h-12 w-12 mx-auto text-orange-300 mb-4" />
            <p className="text-gray-500">No menu items yet</p>
            {canManage && (
              <p className="text-sm text-gray-400 mt-2">Click "Add Item" to add menu items</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
